import { TransactionsSchema } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { SupportedHostAppSchema } from 'renderer/features/products-page/schemas/products.schemas';
import { NewPaymentMethodFormSchema } from 'renderer/features/settings/schemas/paymentMethods.schemas';
import { AuthResponseSchema } from 'renderer/features/auth/schemas/auth.schemas';
import { z } from 'zod';
import { PricesSchema } from './prices.schema';

export const PurchaseOptionsSchema = z.object({
  // productId: z.string(),
  selectedPlan: z.string().optional(),
  // isLoggedIn: z.boolean(),
  // hostApplication: SupportedHostAppSchema,
  // serial: z.string().min(1, 'Serial is required'),
  // platform: z.string(),
  discountCode: z.string().optional(),
  // numberOfCopies: z.number(),
});

export type PurchaseOptions = z.infer<typeof PurchaseOptionsSchema>;

export const LoggedInUserExistingCardSchema = PurchaseOptionsSchema.extend({
  existingPaymentMethodToken: z
    .string()
    .min(1, 'Please choose a payment method'),
});

export const LoggedInUserNewCardSchema = PurchaseOptionsSchema.merge(
  NewPaymentMethodFormSchema,
);

export const AccountFormSchema = z.object({
  createAccount: z.boolean(),
  password: z.string(),
  confirmPassword: z.string(),
  email: z.string().email({ message: 'Invalid email format' }),
  firstName: z.string().min(1, { message: 'First name required' }),
  lastName: z.string().min(1, { message: 'Last name required' }),
  savePayment: z.boolean(),
  makeDefault: z.boolean(),
  isNewPaymentMethod: z.boolean().optional(),
});

const NewAccountFormSchemaUnrefined = AccountFormSchema.extend({
  createAccount: z.literal(true),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must contain at least 8 characters'),
});

export type AccountForm = z.infer<typeof AccountFormSchema>;

export const PublicUserNoAccountSchema =
  LoggedInUserNewCardSchema.merge(AccountFormSchema);

const PublicUserNewAccountSchemaUnrefined = PublicUserNoAccountSchema.merge(
  NewAccountFormSchemaUnrefined,
);

export const PublicUserNewAccountSchema =
  PublicUserNewAccountSchemaUnrefined.refine(
    (data) => data.password === data.confirmPassword,
    {
      message: 'Passwords must match',
      path: ['confirmPassword'],
    },
  );

// Purchase
export const PurchaseFormSchema = PublicUserNewAccountSchemaUnrefined.extend({
  createAccount: z.boolean(),
  isNewPaymentMethod: z.boolean(),
  existingPaymentMethodToken: z.string(),
  newPaymentMethodToken: z.string(),
  paymentProvider: z.string(),
  autoRenewalConsent: z.boolean(),
});

// export const PurchaseRequestSchema = PublicUserNewAccountSchemaUnrefined.extend(
//   {
//     paymentMethod: CardDetailsSchema,
//     createAccount: z.boolean(),
//     // isNewPaymentMethod: z.boolean(),
//     // existingPaymentMethodToken: z.string(),
//     // newPaymentMethodToken: z.string(),
//   },
// );

const UserSchema = z.object({
  // token: z.string().min(1, { message: 'Payment method required' }),
  // isNew: z.boolean(),

  id: z.string().optional(),
  create: z.boolean(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
});

const BillingAddressSchema = z.object({
  name: z.string().optional(),
  streetAddress: z.string().optional(),
  locality: z.string().optional(),
  region: z.string().optional(),
  postalCode: z.string().optional(),
  countryName: z.string().optional(),
});

const PaymentMethodSchema = z.object({
  id: z.string().optional(),
  create: z.boolean().optional(),
  token: z.string().optional(),
  billingAddress: BillingAddressSchema.optional(),
  provider: z.string(),
});

const PurchaseSchema = z.object({
  productId: z.string(),
  priceId: z.string().optional(),
  hostApplication: SupportedHostAppSchema,
  serial: z.string(),
  platform: z.string(),
  discountCode: z.string().optional(),
  numberOfCopies: z.number(),
});

// Billing required for new card
const CardPaymentMethodSchema = z.object({
  provider: z.enum(['stripe', 'braintree']),
  id: z.string().optional(), // not present for new cards
  token: z.string().optional(), // required for new cards
  create: z.boolean().optional(),
  makeDefault: z.boolean().optional(),
  billingAddress: BillingAddressSchema.extend({
    name: z.string().min(1, 'Cardholder name required'),
    streetAddress: z.string().min(1, 'Street address required'),
    locality: z.string().min(1, 'City is required'),
    region: z.string().min(1, 'State/Region is required'),
    postalCode: z.string().min(1, 'Postal code is required'),
    countryName: z.string().min(1, 'Country is required'),
  }),
});

// Existing card, no billing required
const ExistingCardPaymentMethodSchema = z.object({
  provider: z.enum(['stripe', 'braintree']),
  id: z.string().min(1, 'Payment method ID required'),
  create: z.boolean().optional(),
  makeDefault: z.boolean().optional(),
});

// PayPal has no billing at all
const PayPalPaymentMethodSchema = z.object({
  provider: z.literal('paypal'),
});

export const PurchaseRequestSchema = z.object({
  // paymentMethod: CardDetailsSchema,
  // createAccount: z.boolean(),
  // isNewPaymentMethod: z.boolean(),
  // existingPaymentMethodToken: z.string(),
  // newPaymentMethodToken: z.string(),

  user: UserSchema,
  // paymentMethod: PaymentMethodSchema,
  paymentMethod: z.union([
    CardPaymentMethodSchema,
    ExistingCardPaymentMethodSchema,
    PayPalPaymentMethodSchema,
  ]),
  // purchase: PurchaseSchema,
  cartId: z.string().optional(),
  purchases: z.array(
    z.object({
      productId: z.string(),
      numberOfCopies: z.number(),
      platform: z.string().optional(),
      hostApplication: SupportedHostAppSchema,
      serial: z.string().optional(),
      isFinal: z.boolean().optional(),
    }),
  ),
});

// const PurchaseFormSchemaOld = z.object({
//   name: z.string(),
//   email: z.string(),
//   firstName: z.string(),
//   lastName: z.string(),
//   streetAddress: z.string(),
//   locality: z.string(),
//   region: z.string(),
//   regionCode: z.string(),
//   postalCode: z.string(),
//   countryName: z.string(),
//   countryCode: z.string(),
//   password: z.string(),
//   confirmPassword: z.string(),
//   createAccount: z.boolean(),
//   savePayment: z.boolean(),
//   isNewPaymentMethod: z.boolean(),
//   // newPaymentMethodToken: z.string(),
//   existingPaymentMethodToken: z.string(),
//   discountCode: z.string(),
//   hostApplication: SupportedHostAppSchema,
//   platform: z.string(),
//   numberOfCopies: z.number(),
//   selectedPlan: z.string(),
// });

export const PurchaseResponseSchema = z.object({
  customerName: z.string(),
  productName: z.string(),
  prices: PricesSchema,
  transactionId: z.string(),
  purchaseDate: z.string(),
  to: z.string(),
  companyName: z.string(),
  serialNumbers: z.string().array(),
  transactions: TransactionsSchema,
  /** From API: no prior purchase in Google's 540-day lapse window. */
  newCustomer: z.boolean().optional(),
  success: z.boolean().optional(),
  partial: z.boolean().optional(),
  paypalCaptureHeld: z.boolean().optional(),
  confirmationMessage: z.string().optional(),
  user: AuthResponseSchema.shape.user,
  tokens: AuthResponseSchema.shape.tokens,
});

export const RequiresActionResponseSchema = z.object({
  type: z.literal('requires_action'),
  clientSecret: z.string(),
  paymentIntentId: z.string(),
  checkoutSessionId: z.string(),
  transactionIds: z.array(z.string()),
  subscriptionId: z.string().optional(),
  requiresAuthentication: z.boolean().optional(),
});

export const FinalizePurchaseRequestSchema = z.object({
  checkoutSessionId: z.string(),
  paymentIntentId: z.string(),
  transactionIds: z.array(z.string()).optional(),
  createAccount: z.boolean().optional(),
  email: z.string().email().optional(),
});

export type RequiresActionResponse = z.infer<
  typeof RequiresActionResponseSchema
>;
export type FinalizePurchaseRequest = z.infer<
  typeof FinalizePurchaseRequestSchema
>;

export type PurchaseForm = z.infer<typeof PurchaseFormSchema>;

export type PurchaseRequest = z.infer<typeof PurchaseRequestSchema>;
// export interface PurchaseRequestOld extends PurchaseForm {
//   isLoggedIn: boolean;
//   paymentMethod: {
//     isNew: boolean;
//     token: string;
//   };
// }
export type PurchaseResponse = z.infer<typeof PurchaseResponseSchema>;
export type PurchaseConfirmation = PurchaseResponse;

// Braintree client token
export const GetConfigResponseSchema = z.object({
  stripeClientToken: z.string(),
  braintreeClientToken: z.string(),
  recaptchaSiteKey: z.string(),
  paypalClientId: z.string(),
  paypalSecretKey: z.string(),
});

export type GetConfigResponse = z.infer<typeof GetConfigResponseSchema>;
export type GetConfigRequest = void;

// Retrieve exising payment methods
export const ListPaymentMethodsResponseSchema = z
  .object({
    _id: z.string(),
    default: z.boolean(),
    last4: z.string(),
    expirationMonth: z.string(),
    expirationYear: z.string(),
    // customerId: z.string(),
    // isDefault: z.boolean(),
    // token: z.string(),
    cardType: z.string(), // For example: 'credit_card', 'paypal_account', etc.
    // name: z.string(),
    // address: z.string(),
    // city: z.string(),
    // state: z.string(),
    // zip: z.string(),
    // country: z.string(),
    // billingAddress: z.object({
    //   streetAddress: z.string().optional(),
    //   extendedAddress: z.string().optional(),
    //   locality: z.string().optional(),
    //   region: z.string().optional(),
    //   postalCode: z.string().optional(),
    //   countryName: z.string().optional()
    // }).optional(),
  })
  .array();

export type ListPaymentMethodsResponse = z.infer<
  typeof ListPaymentMethodsResponseSchema
>;
export type ListPaymentMethodsRequest = void;

// --- PayPal-specific schemas (no billing required) ---

// Public user, no account creation
export const PublicUserNoAccountPayPalSchema = AccountFormSchema.extend({
  createAccount: z.literal(false),
}).pick({
  email: true,
  firstName: true,
  lastName: true,
  createAccount: true,
});

// Public user, with account creation
export const PublicUserNewAccountPayPalSchema = AccountFormSchema.extend({
  createAccount: z.literal(true),
  password: z.string().min(8, 'Password must contain at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords must match',
  path: ['confirmPassword'],
});

// Authenticated user with PayPal (minimal required)
export const LoggedInUserPayPalSchema = z.object({
  // firstName: z.string().min(1, { message: 'First name required' }),
  // lastName: z.string().min(1, { message: 'Last name required' }),
  // email: z.string().email({ message: 'Invalid email format' }),
  paymentProvider: z.literal('paypal'),
});
